import React, { ReactElement, useCallback, useEffect } from "react";
import { Grid, TableCell } from "@mui/material";
import { itemsListHeadCells } from "./utils";
import Table from "../../../../ui/table/Table";
import useTable from "../../../../ui/table/useTable";
import { prepareQueryParams } from "../../../../utils/common";
import { useParams } from "react-router-dom";
import { HTTP_STATUS_CODES } from "../../../../types/server";
import { handleServerError } from "../../../../utils/http";
import { formatTimestamp } from "../../../../utils/date";
import { Input, Search, SearchIconWrapper } from "../../../../ui/table/styled";
import { useTranslation } from "react-i18next";
import PageWrapper from "../../../../ui/pageWrapper/PageWrapper";
import { route } from "../../../../utils/url";
import { getLOIHistory } from "../../../../api/loi";
import { EMPTY_DATA } from "../../../../constants";
import { Search as SearchIcon } from "react-feather";
import { LOIHistory } from "../../../../types/be/loi";
import FileTableCell from "../../../properties/components/fileTableCell";

const LOIItemHistory = (): ReactElement => {
  const { id } = useParams();
  const { t } = useTranslation();

  const {
    data,
    setData,
    total,
    order,
    orderBy,
    isLoading,
    setIsLoading,
    queryParams,
    error,
    setError,
    rowsPerPage,
    currentPage,
    handleChangeRowsPerPage,
    handleChangePage,
    isConfirmToRemoveModalOpen,
    handleConfirmToRemoveModalClose,
    handleSortChange,
    handleSelectAllClick,
    setTotal,
    handleTableSearch,
  } = useTable<LOIHistory>();

  const fetchData = useCallback(async () => {
    setIsLoading(true);
    const query = {
      loi_id: id,
    };
    const params = prepareQueryParams(queryParams, query);
    const response = await getLOIHistory(params);
    const json = await response.json();

    if (response.status === HTTP_STATUS_CODES.OK) {
      setData(json?.data);
      setTotal(json?.meta?.total);
    } else {
      const { errorMessage } = handleServerError(response);
      setError(errorMessage);
    }

    setIsLoading(false);
  }, [queryParams]);

  const renderRow = (row: LOIHistory): ReactElement => (
    <>
      <TableCell padding="none">
        {row.transaction_manager
          ? `${row.transaction_manager.first_name} ${row.transaction_manager.last_name}`
          : EMPTY_DATA}
      </TableCell>
      <TableCell align="left">
        {row.object_name ? row.object_name : EMPTY_DATA}
      </TableCell>
      <TableCell align="left">
        {row.date ? formatTimestamp(row.date) : EMPTY_DATA}
      </TableCell>
      <TableCell align="left">
        {row.created_at ? formatTimestamp(row.created_at) : EMPTY_DATA}
      </TableCell>
      <FileTableCell file={row.file} />
    </>
  );

  useEffect(() => {
    queryParams && fetchData();
  }, [queryParams]);

  const TableToolbar = (
    <Grid container sx={{ pt: 5 }} justifyContent="flex-end">
      <Grid item>
        <Search>
          <SearchIconWrapper>
            <SearchIcon />
          </SearchIconWrapper>
          <Input placeholder={t("search")} onChange={handleTableSearch} />
        </Search>
      </Grid>
    </Grid>
  );

  const breadcrumbs = [
    {
      to: route("users.company-ownership"),
      name: t("companyOwnership.title"),
    },
  ];

  return (
    <PageWrapper
      title={t("companyOwnership.inventoryHistory")}
      breadcrumbs={breadcrumbs}
    >
      <Table
        data={data}
        total={total}
        currentPage={currentPage}
        order={order}
        orderBy={orderBy}
        error={error}
        onRowsPerPageChange={handleChangeRowsPerPage}
        onPageChange={handleChangePage}
        onSortChange={handleSortChange}
        onSelectAllClick={handleSelectAllClick}
        onConfirmToRemoveModalClose={handleConfirmToRemoveModalClose}
        rowsPerPage={rowsPerPage}
        isLoading={isLoading}
        isConfirmToRemoveModalOpen={isConfirmToRemoveModalOpen}
        listHeadCells={itemsListHeadCells}
        renderRow={renderRow}
        tableToolbar={TableToolbar}
        noDataIsAvailablePlaceholder={"table.noLOIHistoryIsAvailable"}
      />
    </PageWrapper>
  );
};

export default LOIItemHistory;
